import React from "react";
import { Handle, Position } from "@xyflow/react";
import { Trans, useTranslation } from "react-i18next";
import { getNodeRegistryEntry } from "../nodeRegistry";

function getTriggerSubtitle(data) {
    if (data.subtitle) return data.subtitle;
    const triggerType = data.triggerType;
    if (triggerType === "WEBHOOK" && data.publicKey) {
        return `…/webhook/${data.publicKey.slice(0, 8)}`;
    }
    if (triggerType === "EMAIL" && data.publicKey) {
        return `${data.publicKey.slice(0, 8)}…@trigger`;
    }
    if (triggerType === "TIME" && data.timeConfig) {
        if (data.timeConfig.cron) return data.timeConfig.cron;
        if (data.timeConfig.timeOfDay) return `Daily at ${data.timeConfig.timeOfDay}`;
    }
    if (triggerType === "WHATSAPP" && data.whatsappNumber) {
        return data.whatsappNumber;
    }
    return triggerType || "";
}

function TriggerNode({ data, selected }) {
    const { t } = useTranslation();
    const iconKey = data.icon || "trigger";
    const entry = getNodeRegistryEntry(iconKey);
    const Icon = entry.icon;
    const needsConfig =
        data.triggerType === "WHATSAPP" &&
        (!data.allowedPhones || data.allowedPhones.length === 0);
    const borderClass = selected
        ? "border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.15)]"
        : needsConfig
            ? "border-amber-300"
            : "border-gray-200 hover:border-gray-300";

    const subtitle = getTriggerSubtitle(data);

    return (
        <div
            className={`bg-white rounded-lg rounded-l-2xl border ${borderClass} px-3 py-2 min-w-[220px] max-w-[220px] cursor-pointer transition-all duration-200 shadow-sm relative`}
        >
            {needsConfig && (
                <div
                    className="absolute left-1/2 bottom-full mb-3 w-[220px] bg-amber-50 border border-amber-300 rounded-md px-2.5 py-1.5 shadow-[0_2px_10px_rgba(251,191,36,0.35)] animate-sticky-dance"
                    style={{
                        backgroundImage:
                            "repeating-linear-gradient(0deg, transparent, transparent 13px, rgba(251,191,36,0.18) 13px, rgba(251,191,36,0.18) 14px)",
                    }}
                >
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-amber-50 border-r border-b border-amber-300 rotate-45" />
                    <div className="text-[10px] font-semibold text-amber-900 leading-tight">
                        {t("chatInput.whatsappNoteTitle")}
                    </div>
                    <div className="text-[9px] text-amber-800 leading-snug mt-0.5">
                        <Trans
                            i18nKey="chatInput.whatsappNoteBody"
                            components={{ c: <span className="font-mono font-semibold" /> }}
                        />
                    </div>
                </div>
            )}
            <div className="flex items-center gap-2">
                <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: entry.color + "15" }}
                >
                    <Icon size={15} style={{ color: entry.color }} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-900 truncate">
                        {data.title || data.label || "Trigger"}
                    </div>
                    {subtitle && (
                        <div className="text-[10px] text-gray-500 truncate">{subtitle}</div>
                    )}
                </div>
            </div>
            {data.lastReceived && (
                <div className="text-[10px] text-gray-400 mt-1">
                    Last received: {data.lastReceived}
                </div>
            )}
            <Handle type="source" position={Position.Right} className="!bg-gray-300 !w-2 !h-2 !border-0" />
        </div>
    );
}

export default React.memo(TriggerNode);
